import React from "react";
import {
  BrowserRouter,
  Routes as RouterRoutes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { TenantProvider } from "./contexts/TenantContext";
import { LocationProvider } from "./contexts/LocationContext";
import { IntegrationProvider } from "./contexts/IntegrationContext";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import RoleGuard from "components/RoleGuard";
import AppLayout from "./components/navigation/AppLayout";
import NotFound from "pages/NotFound";
import Login from "./pages/login";
import ForgotPassword from "./pages/forgot-password";
import ResetPassword from "./pages/reset-password";
import AcceptInvitation from "./pages/accept-invitation";
import DashboardOverview from "./pages/dashboard-overview";
import DailyTakingsAndTronc from "./pages/daily-takings-and-tronc";
import TroncDistribution from "./pages/tronc-distribution";
import DistributionRules from "./pages/rule-manager";
import DistributionLab from "./pages/distribution-lab";
import Employees from "./pages/employees";
import AuditTrail from "./pages/audit-trail";
import ProfileSettings from "./pages/settings/profile";
import CompanySettings from "./pages/settings/company";
import UserManagement from "./pages/user-management";
import TenantManagement from "./pages/settings/tenants";
import LocationManagement from "./pages/settings/locations";
import SyncLogsPage from "./pages/settings/sync-logs";
import MyTroncReport from "./pages/my-tronc";
import PolicyPage from "./pages/policy";
import Transactions from "./pages/transactions";
import Reports from "./pages/reports";
import ClockRecords from "./pages/clock-records";
import MyClockRecords from "./pages/my-clock-records";

// System Configuration Pages
import JobRolesPage from "./pages/system/job-roles";
import DepartmentsPage from "./pages/system/departments";
import IntegrationsPage from "./pages/system/integrations";
import EligibilityCalculationSettings from "./pages/system/eligibility";
import ErrorLogsPage from "./pages/system/error-logs";
import ScheduledEmailsPage from "./pages/system/scheduled-emails";
import DemoClearPage from "./pages/system/demo-clear";
import PolicyGenerator from "./pages/policy-generator";
import Register from "./pages/register";
import SubscriptionSettings from "./pages/settings/subscription";
import ApiKeysSettings from "./pages/settings/api-keys";

/**
 * Role constants for readability
 * RBAC Matrix:
 * - User:        My TRONC, Policy, Profile
 * - Manager:     Dashboard, Takings, Distribution, Employees, Policy, Profile, Sync Logs
 * - TroncMaster: Dashboard, Takings, Distribution, Employees, Lab, Audit Trail, Policy Generator,
 *       Reports, Transactions, Distribution Rules, Eligibility, Company Details (read-only),
 *       Sync Logs, Profile
 * - TenantAdmin: Everything for their tenant
 * - GlobalAdmin: Everything except creating rules/distributions (backend enforced)
 */
const ALL_ROLES = ["User", "Manager", "TroncMaster", "TenantAdmin", "GlobalAdmin"];
const MANAGER_AND_ABOVE = ["Manager", "TroncMaster", "TenantAdmin", "GlobalAdmin"];
const TRONCMASTER_AND_ABOVE = ["TroncMaster", "TenantAdmin", "GlobalAdmin"];
const ADMIN_AND_ABOVE = ["TenantAdmin", "GlobalAdmin"];
const GLOBAL_ADMIN_ONLY = ["GlobalAdmin"];

/**
 * Smart default redirect based on user role
 */
const DefaultRedirect = () => {
  const { user } = useAuth();
  if (user?.role === "User") {
    return <Navigate to="/my-tronc" replace />;
  }
  return <Navigate to="/dashboard-overview" replace />;
};

const Routes = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <TenantProvider>
          <SubscriptionProvider>
          <LocationProvider>
            <IntegrationProvider>
              <ErrorBoundary>
                <ScrollToTop />
                <RouterRoutes>
                  {/* Public routes */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/accept-invitation" element={<AcceptInvitation />} />
                  <Route path="/register" element={<Register />} />

                  {/* Protected routes with AppLayout */}
                  <Route element={<AppLayout />}>
                    {/* Smart default redirect based on role */}
                    <Route
                      path="/"
                      element={<DefaultRedirect />}
                    />

                    {/* ========================================= */}
                    {/* MANAGER AND ABOVE: Core operational pages */}
                    {/* ========================================= */}
                    <Route
                      path="/dashboard-overview"
                      element={
                        <RoleGuard allowedRoles={MANAGER_AND_ABOVE}>
                          <DashboardOverview />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/daily-takings-and-tronc"
                      element={
                        <RoleGuard allowedRoles={MANAGER_AND_ABOVE}>
                          <DailyTakingsAndTronc />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/tronc-distribution"
                      element={
                        <RoleGuard allowedRoles={MANAGER_AND_ABOVE}>
                          <TroncDistribution />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/employees"
                      element={
                        <RoleGuard allowedRoles={MANAGER_AND_ABOVE}>
                          <Employees />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/clock-records"
                      element={
                        <RoleGuard allowedRoles={MANAGER_AND_ABOVE}>
                          <ClockRecords />
                        </RoleGuard>
                      }
                    />

                    {/* ========================================= */}
                    {/* TRONCMASTER AND ABOVE: Operational + Analysis pages */}
                    {/* ========================================= */}
                    <Route
                      path="/distribution-lab"
                      element={
                        <RoleGuard allowedRoles={TRONCMASTER_AND_ABOVE}>
                          <DistributionLab />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/audit-trail"
                      element={
                        <RoleGuard allowedRoles={TRONCMASTER_AND_ABOVE}>
                          <AuditTrail />
                        </RoleGuard>
                      }
                    />

                    {/* ========================================= */}
                    {/* COMING SOON pages (available to relevant roles) */}
                    {/* ========================================= */}
                    <Route
                      path="/policy-generator"
                      element={
                        <RoleGuard allowedRoles={TRONCMASTER_AND_ABOVE}>
                          <PolicyGenerator />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/policy"
                      element={
                        <RoleGuard allowedRoles={ALL_ROLES}>
                          <PolicyPage />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/reports"
                      element={
                        <RoleGuard allowedRoles={TRONCMASTER_AND_ABOVE}>
                          <Reports />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/transactions"
                      element={
                        <RoleGuard allowedRoles={TRONCMASTER_AND_ABOVE}>
                          <Transactions />
                        </RoleGuard>
                      }
                    />

                    {/* ========================================= */}
                    {/* ALL ROLES: Employee self-service */}
                    {/* ========================================= */}
                    <Route
                      path="/my-tronc"
                      element={
                        <RoleGuard allowedRoles={ALL_ROLES}>
                          <MyTroncReport />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/my-clock-records"
                      element={
                        <RoleGuard allowedRoles={ALL_ROLES}>
                          <MyClockRecords />
                        </RoleGuard>
                      }
                    />

                    {/* ========================================= */}
                    {/* ADMIN AND ABOVE: System Configuration */}
                    {/* ========================================= */}
                    <Route
                      path="/system/job-roles"
                      element={
                        <RoleGuard allowedRoles={MANAGER_AND_ABOVE}>
                          <JobRolesPage />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/system/departments"
                      element={
                        <RoleGuard allowedRoles={TRONCMASTER_AND_ABOVE}>
                          <DepartmentsPage />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/system/eligibility"
                      element={
                        <RoleGuard allowedRoles={TRONCMASTER_AND_ABOVE}>
                          <EligibilityCalculationSettings />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/system/integrations"
                      element={
                        <RoleGuard allowedRoles={GLOBAL_ADMIN_ONLY}>
                          <IntegrationsPage />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/system/error-logs"
                      element={
                        <RoleGuard allowedRoles={GLOBAL_ADMIN_ONLY}>
                          <ErrorLogsPage />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/system/scheduled-emails"
                      element={
                        <RoleGuard allowedRoles={GLOBAL_ADMIN_ONLY}>
                          <ScheduledEmailsPage />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/system/demo-clear"
                      element={
                        <RoleGuard allowedRoles={ADMIN_AND_ABOVE}>
                          <DemoClearPage />
                        </RoleGuard>
                      }
                    />

                    {/* ========================================= */}
                    {/* Settings routes */}
                    {/* ========================================= */}
                    <Route
                      path="/settings/profile"
                      element={
                        <RoleGuard allowedRoles={ALL_ROLES}>
                          <ProfileSettings />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/settings/company"
                      element={
                        <RoleGuard allowedRoles={TRONCMASTER_AND_ABOVE}>
                          <CompanySettings />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/settings/locations"
                      element={
                        <RoleGuard allowedRoles={ADMIN_AND_ABOVE}>
                          <LocationManagement />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/settings/sync-logs"
                      element={
                        <RoleGuard allowedRoles={MANAGER_AND_ABOVE}>
                          <SyncLogsPage />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/settings/users"
                      element={
                        <RoleGuard allowedRoles={ADMIN_AND_ABOVE}>
                          <UserManagement />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/settings/subscription"
                      element={
                        <RoleGuard allowedRoles={ADMIN_AND_ABOVE}>
                          <SubscriptionSettings />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/settings/api-keys"
                      element={
                        <RoleGuard allowedRoles={ADMIN_AND_ABOVE}>
                          <ApiKeysSettings />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/settings/tenants"
                      element={
                        <RoleGuard allowedRoles={GLOBAL_ADMIN_ONLY}>
                          <TenantManagement />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/settings/distribution-rules"
                      element={
                        <RoleGuard allowedRoles={TRONCMASTER_AND_ABOVE}>
                          <DistributionRules />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/settings/job-roles"
                      element={
                        <RoleGuard allowedRoles={MANAGER_AND_ABOVE}>
                          <JobRolesPage />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/settings/departments"
                      element={
                        <RoleGuard allowedRoles={TRONCMASTER_AND_ABOVE}>
                          <DepartmentsPage />
                        </RoleGuard>
                      }
                    />

                    {/* Legacy redirects */}
                    <Route
                      path="/rule-manager"
                      element={
                        <Navigate to="/settings/distribution-rules" replace />
                      }
                    />
                    <Route
                      path="/settings/integrations"
                      element={<Navigate to="/system/integrations" replace />}
                    />
                  </Route>

                  <Route path="*" element={<NotFound />} />
                </RouterRoutes>
              </ErrorBoundary>
            </IntegrationProvider>
          </LocationProvider>
          </SubscriptionProvider>
        </TenantProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default Routes;
